<template>
	<v-slide-group :show-arrows="!isTouchMode">
		<template v-for="column in normalizedColumns" :key="column.field">
			<v-slide-group-item>
				<v-chip :color="isColumnVisible(column) ? 'primary' : undefined" :variant="isColumnVisible(column) ? 'tonal' : 'outlined'" :disabled="isLastVisible(column)" selected-class="text-primary" @click="toggleColumn(column)" class="ma-1">
					<v-icon v-if="chipIcon(column)" :icon="chipIcon(column)" start />
					{{ column.label }}
				</v-chip>
			</v-slide-group-item>
		</template>
	</v-slide-group>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { deviceUtils } from '@/utils/core/deviceUtils';

const props = defineProps({
	columns: {
		type: Array,
		default: () => [],
	},
	modelValue: {
		type: Array,
		default: () => [],
	},
	defaultVisibleColumns: {
		type: Array,
		default: () => [],
	},
	iconMode: { type: String, default: 'check' },
	minSelected: { type: Number, default: 0 },
});

const emit = defineEmits(['update:modelValue']);
const isTouchMode = ref(deviceUtils.isTouchMode());
const onResize = () => {
	isTouchMode.value = deviceUtils.isTouchMode();
};

const visibleColumns = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []));

const normalizedColumns = computed(() => {
	return (Array.isArray(props.columns) ? props.columns : [])
		.map((column, index) => {
			if (typeof column === 'string') {
				return { field: column, label: column };
			}
			return {
				field: column?.field || column?.key || `column-${index}`,
				label: column?.label || column?.name || column?.field || `Column ${index + 1}`,
			};
		})
		.filter((column) => typeof column.field === 'string' && column.field.length > 0);
});

onMounted(() => {
	window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', onResize);
});

watch(
	() => [props.defaultVisibleColumns, props.columns],
	() => {
		applyDefaultVisibleColumns();
	},
	{ deep: false, immediate: true }
);

function applyDefaultVisibleColumns() {
	if (visibleColumns.value.length > 0) {
		return;
	}

	const availableColumns = new Set(normalizedColumns.value.map((column) => column.field));
	const defaultColumns = [...new Set((Array.isArray(props.defaultVisibleColumns) ? props.defaultVisibleColumns : []).filter((field) => availableColumns.has(field)))];

	if (defaultColumns.length === 0) {
		return;
	}

	emitVisibleColumns(defaultColumns);
}

function isColumnVisible(column) {
	return visibleColumns.value.includes(column.field);
}

function emitVisibleColumns(nextColumns) {
	emit('update:modelValue', [...new Set(nextColumns)]);
}

function chipIcon(column) {
	if (props.iconMode === 'visibility') return isColumnVisible(column) ? 'visibility' : 'visibility_off';
	return isColumnVisible(column) ? 'check' : null;
}

function isLastVisible(column) {
	return props.minSelected > 0 && isColumnVisible(column) && visibleColumns.value.length <= props.minSelected;
}

function toggleColumn(column) {
	if (!column?.field) {
		return;
	}

	if (isLastVisible(column)) {
		return;
	}

	const nextColumns = [...visibleColumns.value];
	const existingIndex = nextColumns.indexOf(column.field);

	if (existingIndex >= 0) {
		nextColumns.splice(existingIndex, 1);
	} else {
		nextColumns.push(column.field);
	}

	emitVisibleColumns(nextColumns);
}
</script>
